import React, { useEffect, useState } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import { height, styled } from "@mui/system";
import { Box, Button, Popover, Toolbar, FormControl, MenuItem, Select, InputLabel } from "@mui/material";
import { Form, Formik } from "formik";
import { useField } from "formik";
import InputField from "../../../../components/formFields/InputField";
import {
  setAssessmentDate,
  setInvoiceNumber,
  setMemberName,
  setNaicCode,
  setAssessmentClass,
  setEmailStatus,
  setAssessmentId,
  setGroupName,
  setStatus,
  setStatusIds,
} from "../../../../store/slices/FilterSlice";
import { useDispatch, useSelector } from "react-redux";
 
import DatePickerField from "../../../../shared/components/DatePickerField";
import SelectField from "../../../../shared/components/SelectField";
import { useGetAssessmentsTitleQuery, useGetInvoicesStatusesQuery } from "../../../../services/InsuranceService";
import SearchableSelect from "../../../../components/formFields/SearchableSelect";

const StyledPopover = styled(Popover)({
  "& .MuiPaper-root": {
    padding: "24px",
    minHeight: "500px",
    height:"fit-content",
    width: "300px",
    flexDirection: "column",
    display: "flex",
    justifyContent: "space-between",
  },
});

const FieldContainer = styled(Box)({
  width: "252px",
  margin: "5px 0px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "& .MuiFormLabel-root": {
    marginBottom: "9px",
  },
  "& .MuiInputBase-root": {
    minWidth: "100%",
    minHeight: "30px",
    border: "1px solid #b2b9dc",
    borderRadius: "4px",
    "& input": {
      paddingLeft: "10px",
      paddingRight: "10px",
      paddingBottom: "0px",
      paddingTop: "0px",
    },
  },
});

const StyledButton = styled(Button)(({ theme, isfull }) => ({
  backgroundColor: isfull ? theme.palette.primary.main : "",
  color: isfull ? theme.palette.info.contrastText : theme.palette.primary.main,
  svg: {
    color: isfull
      ? theme.palette.info.contrastText
      : theme.palette.primary.main,
  },
}));

export const assessmentClasses = [
  {
    id: 1,
    code: "Class A",
    name: "Class A",
  },
  {
    id: 2,
    code: "Class B",
    name: "Class B",
  },
];

export const activityOptions = [
  {
    id: 1,
    code: "All",
    name: "All",
  },
  {
    id: 2,
    code: "Active",
    name: "Active",
  },
  {
    id: 3,
    code: "Inactive",
    name: "Inactive",
  },
];

export const emailStatuses = [
  {
    id: 1,
    code: "Ready for Send",
    name: "Ready for Send",
  },
  {
    id: 2,
    code: "Pending",
    name: "Pending",
  },
  {
    id: 3,
    code: "Sent",
    name: "Sent",
  },
  {
    id: 4,
    code: "Failed",
    name: "Failed",
  },
];

export const TableFilter = (props) => {
  const { onApplyFilters, type, isMemberTab, disableFilterReset, hideEmailStatus, defaultOverdue, defaultAllOpen, skipDefaultAssessment, defaultStatus } = props;
  const {
    status,
    statusIds,
    assessmentDate,
    invoiceNumber,
    assessmentClass,
    assessmentId,
    emailStatus,
    groupName
  } = useSelector((state) => state.filter);
  const { data: invoicesStatuses } = useGetInvoicesStatusesQuery("");
  const { data: assessmentsTitles,isSuccess } = useGetAssessmentsTitleQuery("");


  const updateKey = (arr) =>
    arr?.map(({ id, name, ...rest }) => ({ id, code: name, ...rest }));

  const transformedStatuses = updateKey(invoicesStatuses);

  // Add an explicit "All Open" option that signals consumers to filter by invoice.isOpen === true
  const allOpenOption = { id: -999, code: "All Open", name: "All Open" };
  const statusesWithAllOpen = transformedStatuses ? [allOpenOption, ...transformedStatuses] : [allOpenOption];

  // Formik-aware multi-select field for Statuses (stores an array of ids)
  const MultiStatusSelect = ({ name, label, data, width }) => {
    const [field, meta, helpers] = useField(name);
    const { value } = field;
    const { setValue } = helpers;

    return (
      <div>
        <InputLabel id={label}>{label}</InputLabel>
        <FormControl sx={{ width: width || '253px' }}>
          <Select
            labelId={label}
            multiple
            value={value || []}
            onChange={(e) => setValue(e.target.value)}
            renderValue={(selected) => {
              if (!selected || !selected.length) return '';
              return data
                .filter((d) => selected.includes(d.id))
                .map((d) => d.name || d.code)
                .join(', ');
            }}
          >
            {data &&
              data.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name || item.code}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>
    );
  };

  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const onFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Optionally set default status to Overdue only when explicitly requested
  useEffect(() => {
    if (defaultOverdue && !status && invoicesStatuses) {
      dispatch(setStatus('Overdue'));
      onApplyFilters({ status: 'Overdue' });
    }
  }, [defaultOverdue, status, invoicesStatuses]);

  useEffect(() => {
    if (isSuccess) {
      const initial = {};
      // Only set a default assessment when not explicitly skipped
      if (!skipDefaultAssessment && !invoiceNumber && !isMemberTab) {
        const defaultAssessment = assessmentsTitles?.[0] || null;
        dispatch(setAssessmentId(defaultAssessment));
        initial.assessmentId = defaultAssessment;
      }

      // If a specific defaultStatus is provided (e.g. 'Paid'), use it first
      if (defaultStatus && !status && invoicesStatuses) {
        dispatch(setStatus(defaultStatus));
        initial.status = defaultStatus;
      }

      // Support default All Open behavior: pre-select all statuses that are isOpen===true
      else if (defaultAllOpen && !status && invoicesStatuses) {
        const openIds = invoicesStatuses?.filter((s) => s.isOpen).map((s) => s.id) || [];
        dispatch(setStatus('All Open'));
        dispatch(setStatusIds(openIds));
        initial.status = 'All Open';
        initial.statusIds = openIds;
      }

      if (defaultOverdue && !status) {
        dispatch(setStatus('Overdue'));
        initial.status = 'Overdue';
      }

      if (Object.keys(initial).length) {
        onApplyFilters(initial);
      } else if (invoiceNumber) {
        onApplyFilters({ invoiceNumber });
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    return () => {
      onReset(false);
    };
  }, []);

  const onReset = (isNeedFetch) => {
  // Unified NAIC/Member search removed from filter popover; clearing here still resets underlying values
  dispatch(setNaicCode(null));
  dispatch(setMemberName(null));
    dispatch(setStatus(null));
    dispatch(setStatusIds([]));
      dispatch(setAssessmentDate(null));
      dispatch(setAssessmentClass(null));
      dispatch(setAssessmentId(null));
      dispatch(setEmailStatus(null));
      dispatch(setGroupName(null));
      !disableFilterReset && !isMemberTab && dispatch(setInvoiceNumber(null));

    setAnchorEl(null);
    if(isNeedFetch){
      onApplyFilters({
        status: null,
        assessmentDate: null,
        assessmentId: null,
        invoiceNumber: null,
      });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSubmit = (value) => {
    // Determine if the user chose the special "All Open" option
    const isAllOpenSelected = value?.status === "All Open";

    if (isAllOpenSelected) {
      // Build an array of status ids that have isOpen === true from the API result
      const openIds = invoicesStatuses?.filter((s) => s.isOpen).map((s) => s.id) || [];
      // Store the resolved ids in the global filter state
      dispatch(setStatus('All Open'));
      dispatch(setStatusIds(openIds));
      // Replace value so consumers receive statusIds and a visible status label
      value = { ...value, status: 'All Open', statusIds: openIds };
    } else {
      // Normal flow: store any multi-select statusIds from the form
      // Clear any lingering openIds from prior 'All Open' selection since we're in single-select mode
      dispatch(setStatusIds([]));
      // Keep selected single status in sync with Redux so chips and downstream logic are correct
      if (value?.status !== undefined) dispatch(setStatus(value.status));
    }

    dispatch(setAssessmentDate(value.assessmentDate));
    dispatch(setAssessmentClass(value.assessmentClass));
    dispatch(setAssessmentId(value.assessmentId));
    dispatch(setEmailStatus(value.emailStatus));
    dispatch(setInvoiceNumber(value.invoiceNumber));
    dispatch(setGroupName(value.groupName));
    onApplyFilters(value);
    setAnchorEl(null);
  };

  return (
  <Toolbar sx={{ minHeight: "0px !important", marginTop: isMemberTab ? '-50px' : '0px'}}>
      <StyledButton
        onClick={onFilterClick}
        startIcon={<FilterListIcon />}
        variant="outlined"
        isfull={
          status ||
          assessmentDate ||
          invoiceNumber ||
          assessmentClass ||
          emailStatus ||
          assessmentId ||
          groupName
        }
      >
        Filter
      </StyledButton>
      <StyledPopover
        id={"filter-popover"}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Formik
          initialValues={{
            status,
            assessmentDate,
            assessmentId,
            assessmentClass,
            invoiceNumber,
            emailStatus,
            groupName
          }}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {() => (
            <Form id={"filterForm"}>
              <FieldContainer>
                <InputField name={"groupName"} label={"Group Name"} />
              </FieldContainer>
              <FieldContainer>
                <SelectField
                  name={"status"}
                  label={"Status"}
                  data={statusesWithAllOpen}
                  width="253px"
                />
              </FieldContainer>
              <FieldContainer>
                <SelectField
                  name={"assessmentClass"}
                  label={"Assessment Class"}
                  data={assessmentClasses}
                  width="253px"
                />
              </FieldContainer>
              {!hideEmailStatus && (
                <FieldContainer>
                  <SelectField
                    name={"emailStatus"}
                    label={"Email status"}
                    data={emailStatuses}
                    width="253px"
                  />
                </FieldContainer>
              )}
              <FieldContainer>
                <DatePickerField
                  name={"assessmentDate"}
                  label={"Assessment date"}
                  width="100%"
                />
              </FieldContainer>
              <FieldContainer>
                <SearchableSelect
                  name={"assessmentId"}
                  label={"Assessment"}
                  fullWidth
                  multiline
                  options={assessmentsTitles}
                  isHidden
                />
              </FieldContainer>
              <FieldContainer>
                <InputField name={"invoiceNumber"} label={"Invoice Number"} />
              </FieldContainer>
          
              <FieldContainer></FieldContainer>
              <Box sx={{ display: "flex" }}>
                <StyledButton type="submit">Apply</StyledButton>
                <StyledButton onClick={()=>onReset(true)}>Reset</StyledButton>
              </Box>
            </Form>
          )}
        </Formik>
      </StyledPopover>
    </Toolbar>
  );
}
